/**
 * Authentication Context
 * Provides authentication state and methods throughout the app
 */

import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { authService } from '@/services/auth.service';
import type { Applicant } from '@/types';

interface AuthContextType {
  isAuthenticated: boolean;
  user: Applicant | null;
  loading: boolean;
  login: () => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

/**
 * AuthProvider Component
 * Manages authentication state and provides auth methods to child components
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<Applicant | null>(null);
  const [loading, setLoading] = useState(true);

  /**
   * Fetch current user profile from API
   */
  const fetchUser = useCallback(async (): Promise<void> => {
    try {
      const userData = await authService.getCurrentUser();
      setUser(userData);
      setIsAuthenticated(true);
    } catch (error) {
      // If fetching user fails, clear auth state
      console.error('Failed to fetch user:', error);
      setUser(null);
      setIsAuthenticated(false);
      authService.logout();
    }
  }, []);

  /**
   * Check authentication and load user data
   */
  const login = useCallback(async (): Promise<void> => {
    setLoading(true);
    try {
      if (authService.isAuthenticated()) {
        await fetchUser();
      } else {
        setIsAuthenticated(false);
        setUser(null);
      }
    } catch (error) {
      console.error('Login error:', error);
      setIsAuthenticated(false);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [fetchUser]);

  /**
   * Logout user and clear state
   */
  const logout = useCallback((): void => {
    authService.logout();
    setIsAuthenticated(false);
    setUser(null);
  }, []);

  /**
   * Refresh user data from API
   */
  const refreshUser = useCallback(async (): Promise<void> => {
    if (authService.isAuthenticated()) {
      await fetchUser();
    }
  }, [fetchUser]);

  // Check authentication status on mount
  useEffect(() => {
    const initAuth = async () => {
      setLoading(true);
      try {
        if (authService.isAuthenticated()) {
          await fetchUser();
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        setIsAuthenticated(false);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, [fetchUser]);

  const value: AuthContextType = {
    isAuthenticated,
    user,
    loading,
    login,
    logout,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * useAuth Hook
 * Custom hook to access authentication context
 *
 * @throws {Error} If used outside of AuthProvider
 * @returns {AuthContextType} Authentication context with state and methods
 */
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}
